import Link from "next/link";

import { BlogBreadcrumbs } from "@/components/blog/BlogBreadcrumbs";
import BlogPostCard from "@/components/blog/blogPostCard";
import { Pagination } from "@/components/blog/Pagination";
import HeroSection from "@/components/shared/HeroSection";
import Spinner from "@/components/shared/Spinner";
import { topicPath } from "@/lib/blog/paths";
import { BLOG_TOPICS } from "@/lib/blog/topics";
import type { PostPage } from "@/lib/ghost";
import type { BreadcrumbItem } from "@/lib/seo/schema";

type BlogArchiveProps = {
  page: PostPage | null;
  loading: boolean;
  heading: string;
  intro: string;
  breadcrumbs?: BreadcrumbItem[];
  pathFor: (page: number) => string;
  topics?: boolean;
};

const TopicStrip = () => (
  <nav aria-label="Blog topics" className="mx-auto max-w-screen-lg px-6 pb-10">
    <ul className="flex flex-wrap justify-center gap-2">
      {BLOG_TOPICS.map((topic) => (
        <li key={topic.slug}>
          <Link
            href={topicPath(topic.slug)}
            className="inline-block rounded-full border border-gray-200 bg-gray-100 px-3.5 py-1 text-sm text-gray-900 transition-colors hover:bg-gray-200"
          >
            {topic.name}
          </Link>
        </li>
      ))}
    </ul>
  </nav>
);

export const BlogArchive = ({
  page,
  loading,
  heading,
  intro,
  breadcrumbs,
  pathFor,
  topics = false,
}: BlogArchiveProps) => (
  <>
    <HeroSection
      heading={heading}
      body={intro}
      wrapperStyle="py-16 lg:py-20"
      topComponent={breadcrumbs?.length ? <BlogBreadcrumbs items={breadcrumbs} /> : undefined}
    />

    {topics && <TopicStrip />}

    <section className="mx-auto max-w-screen-lg px-6 pb-20">
      <h2 className="sr-only">Articles</h2>

      {page?.posts.length ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {page.posts.map((post, index) => (
            <BlogPostCard key={post.slug} post={post} priority={index < 3} />
          ))}
        </div>
      ) : loading ? (
        <Spinner />
      ) : (
        <p className="py-20 text-center text-2xl font-semibold text-gray-900">
          No blog posts found. Try again later.
        </p>
      )}

      {page && <Pagination page={page.page} pages={page.pages} pathFor={pathFor} />}
    </section>
  </>
);
